/**
 * api.js  —  Mwuïlu API service layer
 * Base URL: https://api.mwuilu.com
 */

const BASE_URL = 'https://api.mwuilu.com'

// ── Token storage ──────────────────────────────────────────────────────────
export function getToken() {
  return localStorage.getItem('mwuilu_token')
}
function saveToken(token) {
  localStorage.setItem('mwuilu_token', token)
}
function clearToken() {
  localStorage.removeItem('mwuilu_token')
}

// ── Core fetch wrapper ─────────────────────────────────────────────────────
async function request(method, path, body = null, isFormData = false) {
  const token   = getToken()
  const headers = { 'Accept': 'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`
  if (!isFormData) headers['Content-Type'] = 'application/json'

  const config = { method, headers }
  if (body !== null) {
    config.body = isFormData ? body : JSON.stringify(body)
  }

  const response = await fetch(`${BASE_URL}${path}`, config)
  if (response.status === 204) return {}

  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    const error     = new Error(data.message || `HTTP ${response.status}`)
    error.status    = response.status
    error.data      = data
    throw error
  }
  return data
}

const get      = (path)            => request('GET',    path)
const post     = (path, body)      => request('POST',   path, body)
const postForm = (path, formData)  => request('POST',   path, formData, true)
const del      = (path)            => request('DELETE', path)

// ═══════════════════════════════════════════════════════════════════════════
//  AUTH
// ═══════════════════════════════════════════════════════════════════════════

export async function login(memberId, accessCode) {
  const data = await post('/auth/login', {
    member_id:   memberId,
    access_code: accessCode,
  })
  if (data.token) saveToken(data.token)
  return data
}

export async function logout() {
  try { await post('/auth/logout') } finally { clearToken() }
}

export async function getMe() {
  if (!getToken()) return { user: null }
  return get('/auth/me').catch(() => { clearToken(); return { user: null } })
}

// ═══════════════════════════════════════════════════════════════════════════
//  PROFILE
// ═══════════════════════════════════════════════════════════════════════════

export async function getProfile() {
  return get('/profile')
}

export async function updateProfile(data) {
  return post('/profile', data)
}

export async function uploadAvatar(file) {
  const fd = new FormData()
  fd.append('avatar', file)
  return postForm('/profile/avatar', fd)
}

export async function updateCompanions(companions) {
  return post('/profile/companions', { companions })
}

// ═══════════════════════════════════════════════════════════════════════════
//  SERVICES
// ═══════════════════════════════════════════════════════════════════════════

export async function getDraft(service) {
  return get(`/services/${service}/draft`)
}

export async function saveDraft(service, payload, draftId = null) {
  const body = { payload }
  if (draftId !== null) body.draft_id = draftId
  return post(`/services/${service}/draft`, body)
}

export async function deleteDraft(service, draftId) {
  return del(`/services/${service}/draft/${draftId}`)
}

export async function submitRequest(service, payload, draftId = null) {
  const body = { payload }
  if (draftId !== null) body.draft_id = draftId
  return post(`/services/${service}/submit`, body)
}

export async function getHistory() {
  return get('/services/history')
}

/**
 * POST /services/passport-upload
 * Uploads a single passport/ID document and returns a stored reference.
 * Returns: { path: string, url: string }
 */
export async function uploadPassportDocument(file) {
  const fd = new FormData()
  fd.append('document', file)
  return postForm('/services/passport-upload', fd)
}

// ═══════════════════════════════════════════════════════════════════════════
//  BLOGS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * GET /blogs — fetch all published blog posts (newest first)
 * Returns: { blogs: Blog[] }
 *
 * Blog shape:
 *   id, title, image_url, date, date_raw, short_content, content
 */
export async function getBlogs() {
  return get('/blogs')
}

/**
 * GET /blogs/:id — fetch a single blog post
 * Returns: { blog: Blog }
 */
export async function getBlog(id) {
  return get(`/blogs/${id}`)
}